import React, {createContext, useCallback, useContext, useState} from 'react';
import {Alert, Slide, Snackbar} from '@mui/material';

const NotificationContext = createContext(null);

export function useNotification() {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
    return ctx;
}

function SlideTransition(props) {
    return <Slide {...props} direction="up"/>;
}

export function NotificationProvider({children}) {
    const [, setQueue] = useState([]);
    const [current, setCurrent] = useState(null);
    const [open, setOpen] = useState(false);

    const push = useCallback((message, severity = 'info') => {
        const id = Date.now() + Math.random();
        setQueue((prev) => [...prev, {id, message, severity}]);
        if (!open) {
            setCurrent({id, message, severity});
            setOpen(true);
        }
    }, [open]);

    const notify = {
        success: useCallback((msg) => push(msg, 'success'), [push]),
        error: useCallback((msg) => push(msg, 'error'), [push]),
        warning: useCallback((msg) => push(msg, 'warning'), [push]),
        info: useCallback((msg) => push(msg, 'info'), [push]),
    };

    const handleClose = useCallback((event, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    }, []);

    const handleExited = useCallback(() => {
        setQueue((prev) => {
            const next = prev.slice(1);
            if (next.length > 0) {
                setCurrent(next[0]);
                setOpen(true);
            } else {
                setCurrent(null);
            }
            return next;
        });
    }, []);

    return (
        <NotificationContext.Provider value={notify}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={5000}
                onClose={handleClose}
                TransitionComponent={SlideTransition}
                TransitionProps={{onExited: handleExited}}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            >
                <Alert
                    variant="filled"
                    severity={current?.severity || 'info'}
                    onClose={handleClose}
                    sx={{width: '100%', alignItems: 'center'}}
                >
                    {current?.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
}